import {MyopMessageKey, MyopMessages} from "../../common/MyopMessages";
import {
    BaseMyopMessage,
    DisposeMessage, ExecuteComponentMethod, MessageDirection,
    CleanupMessage, CleanupReplayMessage,
    MyopBindMessage,
    MyopBindReplayMessage, Ref
} from "../../messages";
import {CleanupHandler, IComponentDefinitionConfig} from "../../common";
import {HostSDK, loaderOptions} from "../hostSDK.ts";

/**
 * Result returned by observeAutoSize callback
 */
export interface AutoSizeResult {
    /** Current width (from content) */
    width: number;
    /** Current height (from content) */
    height: number;
    /** Whether width is being auto-sized */
    autoSizingWidth: boolean;
    /** Whether height is being auto-sized */
    autoSizingHeight: boolean;
}

/**
 * Options for observeAutoSize method
 */
export interface AutoSizeOptions {
    /** Callback called with size info whenever size changes */
    onSizeChange?: (result: AutoSizeResult) => void;
    /** Minimum width constraint */
    minWidth?: number | string;
    /** Maximum width constraint */
    maxWidth?: number | string;
    /** Minimum height constraint */
    minHeight?: number | string;
    /** Maximum height constraint */
    maxHeight?: number | string;
    /** Explicit width set by host - if defined, don't auto-size width */
    explicitWidth?: string | number;
    /** Explicit height set by host - if defined, don't auto-size height */
    explicitHeight?: string | number;
    /** Force auto-size even if container has dimensions */
    forceAutoSize?: boolean;
    /** Min height used by loader to detect collapse (default: 50) */
    loaderMinHeight?: number;
}

//TODO : change IMyopComponent to ref specific impl
type RefTypes = {
    [key: string]: IMyopComponent | Ref
};

export interface IMyopComponent<P = any, R = any> {

    id: string;

    isInitiated: boolean;

    markedForDisposed: boolean;

    setInitiated: () => void

    componentDefinition: IComponentDefinitionConfig

    container: HTMLElement

    //ref components can be element-less
    element?: HTMLElement

    send: (msg: BaseMyopMessage) => CleanupHandler

    bind(messageType: MyopMessageKey, handler: () => boolean): void;

    bind<T extends BaseMyopMessage>(messageType: MyopMessageKey, handler: (message: T) => boolean): void;

    // boolean returned value = handled or not
    onMessageReceived: (msg: BaseMyopMessage) => boolean

    initiated: () => Promise<any>;

    dispose: () => void

    hide(): void;

    show(): void;

    inspect(): void;

    /**
     * Observes the iframe content size and automatically syncs it to the iframe element.
     * Only available for iframe-based components. Uses direct contentDocument access.
     * @param options - Configuration options including size change callback and min/max constraints
     * @returns Cleanup function to stop observing
     */
    observeAutoSize?: (options?: AutoSizeOptions) => CleanupHandler;

    /**
     * Parsed myop:size meta tag from the component HTML.
     * Set by HTMLComponentLoader before iframe creation.
     * Used by the React host to determine sizing mode (fill vs content).
     */
    sizeMeta?: { width?: string | number; height?: string | number; minWidth?: number; maxWidth?: number; minHeight?: number; maxHeight?: number } | null;

    parent?: IMyopComponent;

    props: P

    refs: R
}

function isInheritFrom(obj: any, baseClass: any) {
    if (!obj) {
        return false;
    }

    const proto = Object.getPrototypeOf(obj);

    if (!proto) {
        return false;
    }

    if (proto.constructor.name === baseClass.name) {
        return true;
    }
    return isInheritFrom(proto, baseClass);
}

// TODO : move : 
type messageHandler<T> = (message: T) => boolean;

export abstract class BaseMyopComponent<P = any, R extends RefTypes = RefTypes> implements IMyopComponent<P> {
    id: string = ''

    messageHandlers: Record<string, messageHandler<any>[]> = {};

    element?: HTMLElement

    protected constructor(public componentDefinition: IComponentDefinitionConfig, public container: HTMLElement, options?: loaderOptions) {
        this.id = options?.id || HostSDK.Instance().assignId(componentDefinition);
        const timeout = options?.timeout ? options?.timeout : 5 * 1000;
        setTimeout(() => {
            if (!this.isInitiated && this._whenInitiatedReject && !this._markedForDisposed) {
                this._whenInitiatedReject(`timeout_${timeout} ${this.id}`);
            }
        }, timeout);

        // TODO : auto inspect, control from config
        this.initiated().then(() => {
            // @ts-ignore
            if (window.myop.hostSDK.inspected) {
                this.inspect()
            }
        });
    }

    _markedForDisposed: boolean = false;

    get markedForDisposed(): boolean {
        return this._markedForDisposed;
    }

    set markedForDisposed(value: boolean) {
        if (this._markedForDisposed) {
            throw new Error('InvalidOperationException: The component is already in the process of being disposed. Dispose operation cannot be performed again until the current disposal process is complete.')
        }
        this._markedForDisposed = value;
    }

    abstract hide(): void;

    abstract show(): void;

    abstract inspect(): CleanupHandler;

    //TODO: use bindWhen with return true predicate ...
    bind = <T extends BaseMyopMessage>(messageType: string, handler: (message: T) => boolean) => {
        if (!this.messageHandlers[messageType]) {
            this.messageHandlers[messageType] = []
        }

        // handle duplicate registration
        if (!this.messageHandlers[messageType].includes(handler)) {
            this.messageHandlers[messageType].push(handler);
        }
    }

    bindWhen = <T extends BaseMyopMessage>(messageType: string, predicate: (message: T) => boolean, handler: (message: T) => void): CleanupHandler => {
        if (!handler) {
            throw new Error('can\'t use component.bindWhen without an handler');
        }

        if (!this.messageHandlers[messageType]) {
            this.messageHandlers[messageType] = []
        }

        const handlers = this.messageHandlers[messageType];

        const predicateWrappedHandler = (e: T) => {
            if (predicate(e)) {
                handler(e);
                return true;
            }
            return false;
        };

        //TODO : handle duplicate registration ?
        handlers.push(predicateWrappedHandler);

        return () => {
            const index = handlers.indexOf(predicateWrappedHandler);
            if (index > -1) {
                handlers.splice(index, 1);
            }
        }
    }

    onMessageReceived(incomeMessage: BaseMyopMessage): boolean {
        //console.log('onMessageReceived', document.location.href, incomeMessage, this);
        //TODO : handel special evets ? like initRes ?
        if (incomeMessage.messageType === MyopMessages.ExecuteComponentMethod) {
            const typedMessage = (incomeMessage as ExecuteComponentMethod);

            //keeping this.method() so that context will saved
            if ((this as any)[typedMessage.method]) {
                (this as any)[typedMessage.method](typedMessage.content);
            } else {
                console.log(`method not found ${typedMessage.method} on component`, this)
            }

            return true;
        }

        const handlers = this.messageHandlers[incomeMessage.messageType];
        if (handlers && handlers.length) {
            let handled = false;

            handlers.forEach(handler => {
                handled = handled || handler(incomeMessage)
            })
            return handled;
        } else {
            //TODO : console only when debug config
            //console.log('no handlers found for ', incomeMessage)
        }
        return false;
    }

    setInitiated = () => {
        this.isInitiated = true;

        if (this._whenInitiatedResolve)
            this._whenInitiatedResolve();
    }

    isInitiated: boolean = false;

    _whenInitiatedResolve?: (value?: any) => any;
    _whenInitiatedReject?: (value?: any) => any;

    _whenInitiated: Promise<any> = new Promise((resolve, reject) => {
        this._whenInitiatedResolve = resolve;
        this._whenInitiatedReject = reject;
    });

    initiated = () => {
        return this._whenInitiated;
    }

    private sendCleanupMessage(message: CleanupMessage): void {
        message.source = this.id;
        message.destination = this.id;
        message.direction = MessageDirection.down;

        const cleanupHandler = this.bindWhen(message.replyMessageKey, (msg: MyopBindReplayMessage) => {
            return msg.replayToHandler === message.handlerUniqueId
        }, (m: CleanupReplayMessage) => {
            message.handler(m);
            cleanupHandler();
        });

        const handler = message.handler;
        // @ts-ignore
        delete message.handler

        this.send(message);

        message.handler = handler;
    }

    send(message: BaseMyopMessage): CleanupHandler {
        //messages that exetracted from envelope already have source in them
        if (!message.source)
            message.source = this.id;

        if (!message.destination)
            message.destination = this.id;

        message.direction = MessageDirection.down;

        let cleanupHandler;
        // TODO : fix heavy op..
        //  if (isInheritFrom(message, MyopBindMessage)) {
        // @ts-ignore
        if (message.handler) {
            const typedMessage = (message as MyopBindMessage);
            cleanupHandler = this.bindWhen(typedMessage.replyMessageKey, (msg: MyopBindReplayMessage) => {
                return msg.replayToHandler === typedMessage.handlerUniqueId
            }, typedMessage.handler);

            // @ts-ignore
            delete message.handler
        }

        //TODO: think about that
        if (message.ref) {
            // serializable
            message.ref = message.ref.__nonProxied || message.ref;
        }

        if (message.cleanable) {
            return () => {
                this.sendCleanupMessage(new CleanupMessage(message.id, (replay) => {
                    //TODO : log replay?
                }));

                if (cleanupHandler) {
                    cleanupHandler()
                }
            };
        } else {
            return () => {
            }
        }
    }

    dispose(): void {
        this.markedForDisposed = true;

        if (!this.isInitiated) {
            //TODO : use sdk.logger
            //console.log('disposing before isInitiated', this.id);
            return;
        }
        console.log('disposing component', this.id);

        // //TODO : implement dispose flow
        // Object.keys(this.refs).forEach(refKey=>{
        //     this.refs[refKey].dispose();
        // });

        //TODO : check if there is any time for the o
        // ther side to proccess to message before die ?
        //notify the other side SDK
        this.send(new DisposeMessage())

        const onDisposeHandlers = this.messageHandlers['onDispose']
        if (onDisposeHandlers) {
            onDisposeHandlers.forEach(disposeHandler => {
                //TODO : send null or fix the type ?
                disposeHandler(null);
            })
        }

        //console.log(this.container);
        //this.container.innerHTML = '';
        this.messageHandlers = {}
        this.id += '_disposed';
        this.isInitiated = false;
    }

    props: P = {} as unknown as P;
    refs: R = {} as unknown as R;
}
